/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import PropTypes from 'prop-types'
import {Box, Modal, ModalCloseButton, ModalContent, ModalOverlay} from '@chakra-ui/react'
import ProductView from '../../partials/product-view'
import {useProductViewModal} from '../../hooks/use-product-view-modal'
import {useIntl} from 'react-intl'

/**
 * A Modal that contains Product View
 */
const ProductViewModal = ({product, isOpen, onClose, context, finalFocusRef, ...props}) => {
    const productViewModalData = useProductViewModal(product)
    const {formatMessage} = useIntl()
    // lazy render the modal to have the close button rendered only when the modal is open
    if (!isOpen) {
        return null
    }

    const handleClose = () => {
        onClose()
        finalFocusRef?.current?.focus()
    }

    return (
        <Modal
            variant="productView"
            size="lg"
            isOpen={isOpen}
            onClose={handleClose}
            colorScheme="stokkeCore"
            // We explicitly set trapFocus={false} to disable Chakra's internal FocusLock mechanism.
            // When trapFocus is false, Chakra disables automatic focus management entirely:
            // finalFocusRef will no longer work, returnFocusOnClose has no effect
            // Modal will not trap focus within itself
            // this prevents focusing into add to registry form fields
            trapFocus={false}
        >
            <ModalOverlay />
            <ModalContent
                containerProps={{'data-testid': 'product-view-modal'}}
                aria-label={formatMessage({
                    id: 'product_tile.assistive_msg.product_view',
                    defaultMessage: 'Product view'
                })}
            >
                <ModalCloseButton
                    // focus the button as the first focusable element in the modal,
                    // when the modal opens
                    ref={(el) => {
                        el?.focus()
                    }}
                />
                <Box px={6}>
                    <ProductView
                        showFullLink={true}
                        context={context}
                        imageSize="sm"
                        product={productViewModalData.product}
                        isLoading={productViewModalData.isFetching}
                        onClose={onClose}
                        {...props}
                    />
                </Box>
            </ModalContent>
        </Modal>
    )
}

ProductViewModal.propTypes = {
    finalFocusRef: PropTypes.object,
    isOpen: PropTypes.bool,
    onOpen: PropTypes.func,
    onClose: PropTypes.func,
    product: PropTypes.object,
    isLoading: PropTypes.bool,
    context: PropTypes.string
}

export default ProductViewModal
